import { useState, useRef, useCallback, useEffect, useMemo, Component } from 'react';
import { applyTheme, loadTheme } from './styles/tokens.js';
import { C, USERS } from './styles/tokens.js';
import PdfViewerModal from './components/PdfViewerModal.jsx';
import WhiteboardTab from './components/WhiteboardTab.jsx';
import ProjectTabs from './components/ProjectTabs.jsx';
import ChatPanel from './components/ChatPanel.jsx';
import HelpModal, { SEEN_KEY as HELP_SEEN_KEY } from './components/HelpModal.jsx';
import SnapshotsModal from './components/SnapshotsModal.jsx';
import { useProject } from './lib/useProject.js';
import { optimizePptx } from './api/n8nClient.js';
import { DEMO } from './config.js';

// ============================================================================
// PIUM 협업 보드 v0.3.0 (Teams 탭 앱 사양)
// 6개 탭: 화이트보드 / 파일 / 수정사항 / 일정 / 아카이브 / 변경 로그
// 모든 사용자 액션은 addLog(actor, type, message)로 변경 로그에 자동 누적
// ============================================================================

// ─── 더미 데이터 ────────────────────────────────────────────────────────────
const INIT_CARDS = [
  { id: 1, type: 'note', x: 80,  y: 110, w: 200, h: 110, title: '콘셉트 방향',       content: '미래과학 + 체험형\n감성 스토리텔링 중심',     color: C.gold,   reactions: { '👍': 2 }, comments: [] },
  { id: 2, type: 'task', x: 320, y: 90,  w: 200, h: 110, title: '수정 #2 진행중',    content: '예산표 항목 추가\n박대리 담당',               color: C.blue,   reactions: {}, comments: [{ id: 1, by: '이팀장', text: '오늘까지 가능할까요?' }] },
  { id: 3, type: 'file', x: 560, y: 120, w: 200, h: 110, title: '최종본 v3 확인',    content: '전북과학교육원_v3.pptx\n2026.06.25',         color: C.green,  reactions: { '✅': 3 }, comments: [] },
  { id: 4, type: 'note', x: 120, y: 310, w: 200, h: 110, title: '공간 구성',          content: '입구→몰입존→체험존\n→쉼터→출구',           color: C.purple, reactions: {}, comments: [] },
  { id: 5, type: 'task', x: 400, y: 300, w: 200, h: 110, title: '이미지 교체',        content: 'p.21 해외→국내 사례\n우선순위: 높음',         color: C.red,    reactions: { '❗': 1, '🔥': 1 }, comments: [] },
  { id: 6, type: 'media', x: 640, y: 320, w: 220, h: 130, title: '레퍼런스 영상',     content: 'kbs_미래관_투어.mp4\n00:42 / 02:15',         color: C.orange, reactions: {}, comments: [] },
];

const INIT_AREAS = [
  { id: 1, x: 60,  y: 70,  w: 480, h: 200, name: '콘셉트 영역',  color: C.gold },
  { id: 2, x: 100, y: 290, w: 540, h: 170, name: '실행 트랙',    color: C.blue },
];

const DUMMY_FILES = [
  { id: 1, name: '전북과학교육원_제안서_v3.pptx', size: '38.2 MB', date: '2026.06.25', final: true,  type: 'pptx', folder: '/PIUM/전북과학교육원/',       uploader: '이팀장', version: 'v3.0' },
  { id: 2, name: '전북과학교육원_제안서_v2.pptx', size: '35.1 MB', date: '2026.06.20', final: false, type: 'pptx', folder: '/PIUM/전북과학교육원/',       uploader: '박대리', version: 'v2.0' },
  { id: 3, name: '전북과학교육원_제안서_v1.pptx', size: '32.8 MB', date: '2026.06.15', final: false, type: 'pptx', folder: '/PIUM/전북과학교육원/',       uploader: '이팀장', version: 'v1.0' },
  { id: 4, name: '전시연출_레퍼런스.pdf',         size: '12.4 MB', date: '2026.06.22', final: false, type: 'pdf',  folder: '/PIUM/전북과학교육원/자료/',  uploader: '김과장', version: 'v1.0' },
  { id: 5, name: '공간배치도_r3.dwg',            size: '8.0 MB',  date: '2026.06.24', final: true,  type: 'dwg',  folder: '/PIUM/전북과학교육원/도면/',  uploader: '최주임', version: 'v3.0' },
  { id: 6, name: '예산산출서_초안.xlsx',          size: '1.2 MB',  date: '2026.06.27', final: false, type: 'xlsx', folder: '/PIUM/전북과학교육원/예산/',  uploader: '박대리', version: 'v1.0' },
];

const DUMMY_REVISIONS = [
  { id: 1, page: 12, title: '메인 전시 콘셉트 수정',  requester: '이팀장', status: 'done', date: '06.25', desc: '콘셉트 텍스트 "미래와 과학의 경계"로 변경', file: '전북과학교육원_제안서_v3.pptx' },
  { id: 2, page: 7,  title: '예산표 항목 추가',       requester: '박대리', status: 'ing',  date: '06.26', desc: '운반비 항목 추가 후 합계 재산출 필요',       file: '전북과학교육원_제안서_v3.pptx' },
  { id: 3, page: 21, title: '참고 이미지 교체',       requester: '김과장', status: 'todo', date: '06.27', desc: '해외 사례 이미지 3장 → 국내 사례로 교체',     file: '전북과학교육원_제안서_v3.pptx' },
  { id: 4, page: 5,  title: '표지 타이틀 폰트 변경',  requester: '이팀장', status: 'todo', date: '06.27', desc: '나눔고딕 → 프리텐다드 Bold로 통일',           file: '전북과학교육원_제안서_v3.pptx' },
];

const DUMMY_MILESTONES = [
  { id: 1, label: '킥오프 미팅',       date: '2026-06-10', status: 'done' },
  { id: 2, label: '1차 시안 제출',     date: '2026-06-18', status: 'done' },
  { id: 3, label: '내부 리뷰',         date: '2026-06-25', status: 'done' },
  { id: 4, label: '2차 시안 보완',     date: '2026-06-29', status: 'current' },
  { id: 5, label: '최종본 PT',         date: '2026-07-03', status: 'upcoming' },
  { id: 6, label: '제안서 납품',       date: '2026-07-05', status: 'upcoming' },
];

const TODAY = new Date('2026-06-29');

// ─── 공용 컴포넌트 ─────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const MAP = {
    todo: { label: '대기',   color: C.dim,   bg: C.surface3 },
    ing:  { label: '진행중', color: C.blue,  bg: C.blueBg },
    done: { label: '완료',   color: C.green, bg: C.greenBg },
  };
  const { label, color, bg } = MAP[status] || MAP.todo;
  return (
    <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: bg, color, fontWeight: 700 }}>
      {label}
    </span>
  );
};

const FileIcon = ({ type }) => {
  const icons = { pptx: '📊', pdf: '📄', dwg: '📐', xlsx: '📋', jpg: '🖼️', png: '🖼️', mp4: '🎬' };
  return <span style={{ fontSize: 18 }}>{icons[type] || '📁'}</span>;
};

const Btn = ({ children, onClick, variant = 'ghost', style = {}, disabled = false }) => {
  const base = { padding: '7px 14px', borderRadius: 6, fontSize: 12, cursor: disabled ? 'not-allowed' : 'pointer', fontWeight: 600, transition: 'all 0.15s', opacity: disabled ? 0.5 : 1 };
  const variants = {
    ghost:   { border: `1px solid ${C.border}`, background: 'transparent', color: C.dim },
    outline: { border: `1px solid ${C.border}`, background: 'transparent', color: C.text },
    gold:    { border: 'none', background: C.gold, color: '#000' },
    blue:    { border: 'none', background: C.blue, color: '#fff' },
    danger:  { border: `1px solid ${C.red}44`, background: C.redBg, color: C.red },
  };
  return <button onClick={disabled ? undefined : onClick} disabled={disabled} style={{ ...base, ...variants[variant], ...style }}>{children}</button>;
};

const Toast = ({ msg }) => msg ? (
  <div style={{
    position: 'fixed', bottom: 32, left: '50%', transform: 'translateX(-50%)',
    background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 8,
    padding: '12px 20px', color: C.text, fontSize: 13, fontWeight: 600,
    boxShadow: '0 8px 32px rgba(0,0,0,0.6)', zIndex: 9999,
  }}>{msg}</div>
) : null;

function useToast() {
  const [msg, setMsg] = useState(null);
  const show = useCallback((m) => {
    setMsg(m);
    setTimeout(() => setMsg(null), 2500);
  }, []);
  return [msg, show];
}

// ────────────────────────────────────────────────────────────────────────────
// 1) 화이트보드 탭
// ────────────────────────────────────────────────────────────────────────────
// ────────────────────────────────────────────────────────────────────────────
// 2) 파일 탭
// ────────────────────────────────────────────────────────────────────────────
function FilesTab({ addLog, sendToWhiteboard }) {
  const [files, setFiles] = useState(DUMMY_FILES);
  const [hovering, setHovering] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [pptFile, setPptFile] = useState(null);
  const [toast, showToast] = useToast();

  const setFinal = (id) => {
    const f = files.find(x => x.id === id);
    setFiles(prev => prev.map(x => ({ ...x, final: x.id === id })));
    showToast('최종 버전으로 지정되었습니다.');
    addLog('윤대표', 'file', `최종 지정: ${f.name}`);
  };

  const handleFiles = (list) => {
    if (!list || list.length === 0) return;
    const ext = (name) => (name.split('.').pop() || '').toLowerCase();
    const fmtSize = (b) => b < 1024 ? `${b} B` : b < 1048576 ? `${(b/1024).toFixed(1)} KB` : `${(b/1048576).toFixed(1)} MB`;
    const today = new Date().toISOString().slice(0, 10).replace(/-/g, '.');
    const newFiles = list.map((f, i) => ({
      id: Date.now() + i,
      name: f.name,
      size: fmtSize(f.size),
      date: today,
      final: false,
      type: ext(f.name),
      folder: '/PIUM/전북과학교육원/',
      uploader: '윤대표',
      version: 'v1.0',
      blob: f,
    }));
    setFiles(prev => [...newFiles, ...prev]);
    showToast(`${list.length}개 파일 업로드 완료`);
    list.forEach(f => addLog('윤대표', 'file', `업로드: ${f.name} (${fmtSize(f.size)})`));
  };

  const reupload = (f) => {
    // 실제 파일 재선택을 위한 input 생성
    const input = document.createElement('input');
    input.type = 'file';
    input.onchange = (e) => {
      const newFile = e.target.files?.[0];
      if (!newFile) return;
      const fmtSize = (b) => b < 1024 ? `${b} B` : b < 1048576 ? `${(b/1024).toFixed(1)} KB` : `${(b/1048576).toFixed(1)} MB`;
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, '.');
      setFiles(prev => prev.map(x => x.id === f.id ? { ...x, name: newFile.name, size: fmtSize(newFile.size), date: today, blob: newFile } : x));
      showToast(`재업로드 완료: ${newFile.name}`);
      addLog('윤대표', 'file', `재업로드: ${newFile.name}`);
    };
    input.click();
  };

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1200 }}>
      {/* 드롭존 */}
      <label
        onDragOver={(e) => { e.preventDefault(); setHovering(true); }}
        onDragLeave={() => setHovering(false)}
        onDrop={(e) => {
          e.preventDefault(); setHovering(false);
          const list = Array.from(e.dataTransfer.files || []);
          handleFiles(list);
        }}
        style={{
          display: 'block',
          border: `2px dashed ${hovering ? C.gold : C.border}`,
          borderRadius: 10, padding: '28px 24px', textAlign: 'center', marginBottom: 24,
          background: hovering ? C.goldBg : C.surface,
          transition: 'all 0.2s', cursor: 'pointer',
        }}>
        <input type="file" multiple style={{ display: 'none' }}
          onChange={(e) => { handleFiles(Array.from(e.target.files || [])); e.target.value = ''; }} />
        <div style={{ fontSize: 32, marginBottom: 8 }}>📂</div>
        <div style={{ color: hovering ? C.gold : C.dim, fontSize: 14, fontWeight: 600 }}>
          {hovering ? '파일을 여기 놓으세요' : '파일을 드래그하거나 클릭해서 업로드 (다중 선택 가능)'}
        </div>
        <div style={{ color: C.dim, fontSize: 12, marginTop: 4 }}>PPT, PDF, DWG, 이미지, Excel 등 모든 형식</div>
      </label>

      {/* 파일 목록 */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {files.map(file => (
          <div key={file.id} style={{
            display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px',
            background: C.surface, borderRadius: 8,
            border: file.final ? `1px solid ${C.gold}44` : `1px solid ${C.border}`,
            position: 'relative', overflow: 'hidden',
          }}>
            {file.final && (
              <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: C.gold }} />
            )}
            <div style={{ marginLeft: file.final ? 6 : 0 }}>
              <FileIcon type={file.type} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13, color: C.text, fontWeight: 600 }}>{file.name}</span>
                <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 3, background: C.surface3, color: C.dim, fontWeight: 700, letterSpacing: 0.5 }}>{file.version}</span>
                {file.final && (
                  <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, background: C.goldBg, color: C.gold, fontWeight: 800, letterSpacing: 1 }}>FINAL</span>
                )}
              </div>
              <div style={{ fontSize: 11, color: C.dim, marginTop: 3, display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ color: USERS[file.uploader] || C.dim }}>👤 {file.uploader}</span>
                <span>·</span>
                <span>{file.size}</span>
                <span>·</span>
                <span>{file.date}</span>
                <span>·</span>
                <span style={{ fontFamily: 'monospace' }}>{file.folder}</span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {file.type === 'pdf' && <Btn variant="outline" onClick={() => setPdfFile(file)}>📄 PDF</Btn>}
              {file.type === 'pptx' && <Btn variant="outline" onClick={() => setPptFile(file)}>⚙ 최적화</Btn>}
              <Btn variant="gold" onClick={() => { sendToWhiteboard?.(file); showToast(`화이트보드에 추가: ${file.name}`); addLog('윤대표', 'file', `화이트보드로: ${file.name}`); }}>🖼 보드로</Btn>
              <Btn onClick={() => { showToast('파일 열기 (데모)'); addLog('윤대표', 'file', `열기: ${file.name}`); }}>열기</Btn>
              <Btn onClick={() => reupload(file)}>↻ 재업로드</Btn>
              {!file.final && <Btn variant="gold" onClick={() => setFinal(file.id)}>최종 지정</Btn>}
            </div>
          </div>
        ))}
      </div>

      {pdfFile && <PdfViewerModal file={pdfFile} onClose={() => setPdfFile(null)} />}
      {pptFile && <PptOptimizerModal file={pptFile} onClose={() => setPptFile(null)} addLog={addLog} />}
      <Toast msg={toast} />
    </div>
  );
}

function PptOptimizerModal({ file, onClose, addLog }) {
  const [checks, setChecks] = useState({
    typo: true,
    spaces: true,
    images: true,
    margin: false,
  });
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);

  const items = [
    { key: 'typo',   label: '오탈자 교정 (Claude API)',          desc: '문장 단위로 LLM 호출하여 오타·문법 교정 후 결과 비교' },
    { key: 'spaces', label: '더블 스페이스 제거',                 desc: 're.sub(r"  +", " ", text) 정규식 일괄 적용' },
    { key: 'images', label: '이미지 → /images/ 폴더 추출',        desc: '모든 슬라이드 이미지를 동명 폴더로 분리 저장' },
    { key: 'margin', label: '여백 표준화 (테두리 0.5cm)',          desc: '모든 슬라이드 텍스트 박스 여백 통일' },
  ];

  const [result, setResult] = useState(null);
  const [errMsg, setErrMsg] = useState(null);
  const run = async () => {
    setRunning(true); setErrMsg(null);
    try {
      if (DEMO) {
        // DEMO 모드: 2초 시뮬레이션
        await new Promise(r => setTimeout(r, 2000));
        setResult({ ok: true, fix_count: 14, image_count: 32, slide_count: 24 });
      } else {
        const r = await optimizePptx(file, checks);
        setResult(r);
      }
      setDone(true);
      addLog('윤대표', 'optimize', `PPT 최적화 완료: ${file.name}`);
    } catch (e) {
      setErrMsg(e.message);
      addLog('윤대표', 'optimize', `최적화 실패: ${file.name} - ${e.message}`);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: C.surface, borderRadius: 14, width: 520, padding: 28, border: `1px solid ${C.border}`, boxShadow: '0 24px 64px rgba(0,0,0,0.7)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
          <div>
            <div style={{ fontSize: 10, color: C.gold, fontWeight: 800, letterSpacing: 1.5 }}>PPT 최적화</div>
            <div style={{ fontSize: 16, color: C.text, fontWeight: 800, marginTop: 4 }}>{file.name}</div>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: C.dim, fontSize: 22, cursor: 'pointer' }}>×</button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 18 }}>
          {items.map(it => (
            <label key={it.key} style={{
              display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 14px',
              background: checks[it.key] ? C.goldBg : C.surface2,
              border: `1px solid ${checks[it.key] ? C.gold + '44' : C.border}`,
              borderRadius: 8, cursor: 'pointer',
            }}>
              <input type="checkbox" checked={checks[it.key]}
                onChange={e => setChecks(c => ({ ...c, [it.key]: e.target.checked }))}
                style={{ marginTop: 2, accentColor: C.gold }} />
              <div>
                <div style={{ fontSize: 13, color: C.text, fontWeight: 700 }}>{it.label}</div>
                <div style={{ fontSize: 11, color: C.dim, marginTop: 3, lineHeight: 1.5 }}>{it.desc}</div>
              </div>
            </label>
          ))}
        </div>

        {done && result && (
          <div style={{ padding: '12px 14px', background: C.greenBg, borderRadius: 6, marginBottom: 14, border: `1px solid ${C.green}44`, color: C.green, fontSize: 12, fontWeight: 700 }}>
            ✓ 완료 · 교정 {result.fix_count}건 · 이미지 {result.image_count}개 · 슬라이드 {result.slide_count}장
            {DEMO && <div style={{ marginTop: 4, color: C.dim, fontWeight: 400 }}>(DEMO 모드 - .env.local에 VITE_AAD_CLIENT_ID 설정 시 실 호출)</div>}
          </div>
        )}
        {errMsg && (
          <div style={{ padding: '10px 14px', background: C.redBg, borderRadius: 6, marginBottom: 14, border: `1px solid ${C.red}44`, color: C.red, fontSize: 12, fontWeight: 700 }}>
            ❌ {errMsg}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Btn onClick={onClose}>닫기</Btn>
          <Btn variant="gold" onClick={run} disabled={running || done || !Object.values(checks).some(Boolean)}>
            {running ? '⏳ 처리 중...' : done ? '✓ 완료' : '⚙ n8n 워크플로우 실행'}
          </Btn>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// 3) 수정사항 탭
// ────────────────────────────────────────────────────────────────────────────
function RevisionsTab({ addLog }) {
  const [selected, setSelected] = useState(null);
  const [revisions, setRevisions] = useState(DUMMY_REVISIONS);
  const [toast, showToast] = useToast();

  const markDone = (id) => {
    const r = revisions.find(x => x.id === id);
    setRevisions(prev => prev.map(x => x.id === id ? { ...x, status: 'done' } : x));
    setSelected(null);
    addLog(r.requester, 'revision', `완료 처리: ${r.title}`);
  };

  const statusCount = (s) => revisions.filter(r => r.status === s).length;

  const goToSlide = (file, page) => {
    // 안전한 fallback: SharePoint webUrl 새 탭 열기 (Windows "안전하지 않은 콘텐츠" 경고 회피)
    const tenant = 'piuda.sharepoint.com';
    const sitePath = 'sites/PIUMHQ/Shared Documents/04-제안';
    const webUrl = `https://${tenant}/${sitePath}/${encodeURIComponent(file)}`;
    // 새 탭에서 SharePoint Online으로 직접 (브라우저 뷰어 또는 자동으로 PowerPoint 데스크톱 호출)
    const slideAnchor = page ? `?slide=${page}` : '';
    window.open(webUrl + slideAnchor, '_blank', 'noopener,noreferrer');
    showToast(`SharePoint 열기: ${file} p.${page}`);
    addLog('윤대표', 'revision', `SharePoint 열기: ${file} p.${page}`);
  };

  const openFolder = (file) => {
    showToast(`탐색기 열기: ${file}`);
    addLog('윤대표', 'revision', `탐색기 열기: ${file}`);
  };

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1200 }}>
      <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
        {[['전체', revisions.length, C.dim, C.surface2], ['진행중', statusCount('ing'), C.blue, C.blueBg], ['대기', statusCount('todo'), C.dim, C.surface2], ['완료', statusCount('done'), C.green, C.greenBg]].map(([l, n, c, bg]) => (
          <div key={l} style={{ padding: '10px 20px', background: bg, borderRadius: 8, border: `1px solid ${C.border}` }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: c }}>{n}</div>
            <div style={{ fontSize: 11, color: C.dim, marginTop: 2 }}>{l}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {revisions.map(rev => (
          <div key={rev.id} onClick={() => setSelected(rev)} style={{
            display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px',
            background: C.surface, borderRadius: 8, border: `1px solid ${C.border}`, cursor: 'pointer',
          }}>
            {/* PDF 페이지 미니 thumbnail */}
            <div style={{
              width: 56, height: 72, borderRadius: 4, background: '#fff',
              position: 'relative', overflow: 'hidden', flexShrink: 0,
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)', border: `1px solid ${C.border}`,
            }}>
              {/* 페이지 콘텐츠 mockup - 회색 라인들 */}
              <div style={{ position: 'absolute', top: 8, left: 6, right: 6, height: 4, background: '#999', borderRadius: 1 }} />
              <div style={{ position: 'absolute', top: 16, left: 6, right: 14, height: 2, background: '#ccc', borderRadius: 1 }} />
              <div style={{ position: 'absolute', top: 22, left: 6, right: 10, height: 2, background: '#ccc', borderRadius: 1 }} />
              <div style={{ position: 'absolute', top: 30, left: 6, width: 24, height: 16, background: '#ddd', borderRadius: 2 }} />
              <div style={{ position: 'absolute', top: 30, left: 32, right: 6, height: 2, background: '#ccc', borderRadius: 1 }} />
              <div style={{ position: 'absolute', top: 36, left: 32, right: 8, height: 2, background: '#ccc', borderRadius: 1 }} />
              <div style={{ position: 'absolute', top: 52, left: 6, right: 6, height: 2, background: '#ccc', borderRadius: 1 }} />
              <div style={{ position: 'absolute', top: 58, left: 6, right: 14, height: 2, background: '#ccc', borderRadius: 1 }} />
              {/* 페이지 번호 오버레이 */}
              <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '2px 4px', background: 'rgba(0,0,0,0.7)', color: '#fff', fontSize: 9, fontWeight: 800, textAlign: 'center', letterSpacing: 0.5 }}>
                p.{rev.page}
              </div>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
                <span style={{ fontSize: 13, color: C.text, fontWeight: 700 }}>{rev.title}</span>
                <StatusBadge status={rev.status} />
              </div>
              <div style={{ fontSize: 12, color: C.dim }}>{rev.desc}</div>
            </div>
            <button onClick={(e) => { e.stopPropagation(); goToSlide(rev.file, rev.page); }}
              style={{ padding: '7px 12px', borderRadius: 6, border: `1px solid ${C.gold}44`, background: C.goldBg, color: C.gold, fontSize: 11, cursor: 'pointer', fontWeight: 700, flexShrink: 0 }}>
              ↗ p.{rev.page}
            </button>
            <div style={{ textAlign: 'right', flexShrink: 0, minWidth: 60 }}>
              <div style={{ fontSize: 12, color: USERS[rev.requester] || C.text }}>{rev.requester}</div>
              <div style={{ fontSize: 11, color: C.dim, marginTop: 2 }}>{rev.date}</div>
            </div>
          </div>
        ))}
      </div>

      {selected && (
        <div onClick={() => setSelected(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: C.surface, borderRadius: 14, width: 540, padding: 28, border: `1px solid ${C.border}`, boxShadow: '0 24px 64px rgba(0,0,0,0.6)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 10, color: C.gold, fontWeight: 800, letterSpacing: 1.5, marginBottom: 6 }}>수정사항 상세</div>
                <div style={{ fontSize: 17, color: C.text, fontWeight: 800 }}>{selected.title}</div>
                <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
                  <StatusBadge status={selected.status} />
                  <span style={{ fontSize: 11, color: C.dim }}>p.{selected.page} · {selected.requester} · {selected.date}</span>
                </div>
              </div>
              <button onClick={() => setSelected(null)} style={{ background: 'transparent', border: 'none', color: C.dim, fontSize: 22, cursor: 'pointer' }}>×</button>
            </div>

            <div style={{
              background: C.surface2, borderRadius: 8, height: 180,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              marginBottom: 18, border: `1px solid ${C.border}`, gap: 8,
            }}>
              <span style={{ fontSize: 40 }}>📄</span>
              <span style={{ fontSize: 13, color: C.dim }}>{selected.file}</span>
              <span style={{ fontSize: 12, color: C.dim2 }}>페이지 {selected.page} 미리보기</span>
            </div>

            <div style={{ background: C.surface2, borderRadius: 8, padding: '12px 16px', marginBottom: 20 }}>
              <div style={{ fontSize: 10, color: C.dim, marginBottom: 5, fontWeight: 700, letterSpacing: 1 }}>수정 내용</div>
              <div style={{ fontSize: 13, color: C.text, lineHeight: 1.7 }}>{selected.desc}</div>
            </div>

            <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <Btn onClick={() => setSelected(null)}>닫기</Btn>
              <Btn variant="outline" onClick={() => openFolder(selected.file)}>📁 탐색기 열기</Btn>
              <Btn variant="outline" onClick={() => goToSlide(selected.file, selected.page)}>↗ 원고 p.{selected.page} 바로가기</Btn>
              {selected.status !== 'done' && <Btn variant="gold" onClick={() => markDone(selected.id)}>✓ 완료 처리</Btn>}
            </div>
          </div>
        </div>
      )}

      <Toast msg={toast} />
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// 4) 일정 탭
// ────────────────────────────────────────────────────────────────────────────
function ScheduleTab({ projects, currentId, setCurrentId }) {
  const milestones = DUMMY_MILESTONES;
  const total = milestones.length;
  const doneCount = milestones.filter(m => m.status === 'done').length;
  const progress = Math.round((doneCount / total) * 100);

  const finalDate = new Date(milestones[milestones.length - 1].date);
  const dDay = Math.ceil((finalDate - TODAY) / (1000 * 60 * 60 * 24));

  const next = milestones.find(m => m.status === 'current') || milestones.find(m => m.status === 'upcoming');
  const nextDDay = next ? Math.ceil((new Date(next.date) - TODAY) / (1000 * 60 * 60 * 24)) : null;

  // 모든 프로젝트의 마감일 카드 통합 - 다가오는 순
  const allDeadlines = (projects || [])
    .flatMap(p => (p.data?.dates || []).map(d => ({ ...d, projectId: p.id, projectName: p.name })))
    .map(d => ({ ...d, dday: Math.ceil((new Date(d.date) - TODAY) / 86400000) }))
    .filter(d => d.dday >= -7)
    .sort((a, b) => a.dday - b.dday)
    .slice(0, 6);

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1200, display: 'grid', gridTemplateColumns: '1fr 360px', gap: 24 }}>
      {/* ── 좌측: 마일스톤 타임라인 ── */}
      <div>
        <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
          <div style={{ flex: 1, padding: '18px 22px', background: C.surface, border: `1px solid ${C.gold}44`, borderRadius: 12 }}>
            <div style={{ fontSize: 11, color: C.dim, fontWeight: 700, letterSpacing: 1.2 }}>최종 납품까지</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginTop: 6 }}>
              <span style={{ fontSize: 32, color: C.gold, fontWeight: 800 }}>D-{dDay}</span>
              <span style={{ fontSize: 11, color: C.dim, marginLeft: 8 }}>{milestones[milestones.length - 1].date}</span>
            </div>
          </div>
          <div style={{ flex: 1.5, padding: '18px 22px', background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
              <span style={{ fontSize: 11, color: C.dim, fontWeight: 700, letterSpacing: 1.2 }}>전체 진행률</span>
              <span style={{ fontSize: 18, color: C.text, fontWeight: 800 }}>{progress}%</span>
            </div>
            <div style={{ height: 8, background: C.surface3, borderRadius: 4, overflow: 'hidden' }}>
              <div style={{ width: `${progress}%`, height: '100%', background: `linear-gradient(90deg, ${C.gold}, ${C.green})`, transition: 'width 0.4s' }} />
            </div>
            <div style={{ fontSize: 11, color: C.dim, marginTop: 8 }}>{doneCount} / {total} 완료</div>
          </div>
        </div>

        <div style={{ fontSize: 11, color: C.dim, fontWeight: 700, marginBottom: 12, letterSpacing: 1 }}>MILESTONE TIMELINE</div>
        <div style={{ position: 'relative', paddingLeft: 28 }}>
          <div style={{ position: 'absolute', left: 11, top: 14, bottom: 14, width: 2, background: C.border }} />
          {milestones.map((m, i) => {
            const dotColor = m.status === 'done' ? C.green : m.status === 'current' ? C.gold : C.dim2;
            const bg       = m.status === 'done' ? C.greenBg : m.status === 'current' ? C.goldBg : C.surface;
            const label    = m.status === 'done' ? '완료' : m.status === 'current' ? '진행중' : '예정';
            return (
              <div key={m.id} style={{ position: 'relative', marginBottom: 12, padding: '14px 18px', background: bg, border: `1px solid ${dotColor}33`, borderRadius: 8 }}>
                <div style={{ position: 'absolute', left: -22, top: 18, width: 14, height: 14, borderRadius: '50%', background: dotColor, border: `3px solid ${C.bg}`, boxShadow: m.status === 'current' ? `0 0 0 4px ${C.goldBg}` : 'none' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 14, color: C.text, fontWeight: 700 }}>{m.label}</span>
                      <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 4, background: `${dotColor}22`, color: dotColor, fontWeight: 800 }}>{label}</span>
                    </div>
                    <div style={{ fontSize: 11, color: C.dim, marginTop: 4 }}>{m.date}</div>
                  </div>
                  <div style={{ fontSize: 11, color: C.dim2 }}>{i + 1} / {total}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── 우측: 다음 마일스톤 + 모든 마감일 위젯 ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {next && (
          <div style={{ padding: '22px 22px', background: `linear-gradient(135deg, ${C.gold}22, transparent)`, border: `2px solid ${C.gold}66`, borderRadius: 12 }}>
            <div style={{ fontSize: 10, color: C.gold, fontWeight: 800, letterSpacing: 1.5, marginBottom: 8 }}>NEXT MILESTONE</div>
            <div style={{ fontSize: 22, color: C.text, fontWeight: 800, marginBottom: 6 }}>{next.label}</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 14 }}>
              <span style={{ fontSize: 44, color: C.gold, fontWeight: 900, lineHeight: 1 }}>
                {nextDDay === 0 ? 'TODAY' : nextDDay > 0 ? `D-${nextDDay}` : `D+${Math.abs(nextDDay)}`}
              </span>
              <span style={{ fontSize: 12, color: C.dim }}>{next.date}</span>
            </div>
          </div>
        )}

        {/* 모든 프로젝트의 마감일 통합 */}
        <div style={{ padding: '16px 18px', background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12 }}>
          <div style={{ fontSize: 10, color: C.dim, fontWeight: 800, letterSpacing: 1.5, marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>📅 다가오는 마감일</span>
            <span style={{ color: C.dim2, fontWeight: 600 }}>{allDeadlines.length}건</span>
          </div>
          {allDeadlines.length === 0 ? (
            <div style={{ fontSize: 12, color: C.dim, padding: '12px 0', textAlign: 'center' }}>등록된 마감일이 없습니다</div>
          ) : allDeadlines.map(d => {
            const urgent = d.dday >= 0 && d.dday <= 3;
            const past = d.dday < 0;
            const col = past ? C.dim : (urgent ? C.red : (d.color || C.gold));
            return (
              <div key={d.id} onClick={() => { setCurrentId(d.projectId); }}
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 10px', borderRadius: 6, cursor: 'pointer', borderLeft: `3px solid ${col}` }}
                onMouseEnter={e => e.currentTarget.style.background = C.surface2}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <div style={{ fontSize: 18, color: col, fontWeight: 900, minWidth: 52, textAlign: 'center' }}>
                  {past ? `D+${Math.abs(d.dday)}` : (d.dday === 0 ? 'D-DAY' : `D-${d.dday}`)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, color: C.text, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.label}</div>
                  <div style={{ fontSize: 10, color: C.dim, marginTop: 2 }}>{d.date} · {d.projectName}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// 5) 아카이브 탭
// ────────────────────────────────────────────────────────────────────────────
function ArchiveTab({ addLog }) {
  const [selected, setSelected] = useState(new Set([1, 5]));
  const [target, setTarget] = useState('\\\\NAS\\PIUM\\납품\\전북과학교육원_2026\\');
  const [done, setDone] = useState(false);
  const [toast, showToast] = useToast();

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
    setDone(false);
  };

  const runCopy = () => {
    if (selected.size === 0) { showToast('파일을 1개 이상 선택하세요'); return; }
    setDone(true);
    showToast(`${selected.size}개 파일 복사 시뮬레이션 완료`);
    addLog('윤대표', 'archive', `아카이브: ${selected.size}개 파일 → ${target}`);
  };

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1200 }}>
      <div style={{ fontSize: 13, color: C.text, fontWeight: 700, marginBottom: 6 }}>📦 최종본 아카이브</div>
      <div style={{ fontSize: 12, color: C.dim, marginBottom: 20 }}>납품용 폴더로 선택한 파일을 일괄 복사합니다.</div>

      {/* 폴더 입력 */}
      <div style={{ marginBottom: 18 }}>
        <label style={{ fontSize: 11, color: C.dim, fontWeight: 700, letterSpacing: 1 }}>대상 폴더</label>
        <input value={target} onChange={e => setTarget(e.target.value)}
          style={{ width: '100%', marginTop: 6, padding: '10px 14px', background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, fontSize: 13, fontFamily: 'monospace', outline: 'none' }} />
      </div>

      {/* 파일 체크박스 */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 20 }}>
        {DUMMY_FILES.map(f => (
          <label key={f.id} style={{
            display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px',
            background: selected.has(f.id) ? C.goldBg : C.surface,
            border: `1px solid ${selected.has(f.id) ? C.gold + '44' : C.border}`,
            borderRadius: 8, cursor: 'pointer',
          }}>
            <input type="checkbox" checked={selected.has(f.id)} onChange={() => toggle(f.id)} style={{ accentColor: C.gold }} />
            <FileIcon type={f.type} />
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 13, color: C.text, fontWeight: 600 }}>{f.name}</span>
                {f.final && <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 3, background: C.goldBg, color: C.gold, fontWeight: 800 }}>FINAL</span>}
                <span style={{ fontSize: 10, padding: '1px 5px', borderRadius: 3, background: C.surface3, color: C.dim }}>{f.version}</span>
              </div>
              <div style={{ fontSize: 11, color: C.dim, marginTop: 2 }}>{f.size} · {f.date} · {f.folder}</div>
            </div>
          </label>
        ))}
      </div>

      {/* 실행 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8 }}>
        <div style={{ fontSize: 12, color: C.dim }}>{selected.size}개 파일 선택됨 · 총 {DUMMY_FILES.filter(f => selected.has(f.id)).reduce((s, f) => s + parseFloat(f.size), 0).toFixed(1)} MB</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn onClick={() => setSelected(new Set(DUMMY_FILES.filter(f => f.final).map(f => f.id)))}>FINAL만 선택</Btn>
          <Btn variant="gold" onClick={runCopy}>{done ? '✓ 복사 완료' : '📦 복사 실행'}</Btn>
        </div>
      </div>

      <Toast msg={toast} />
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// 6) 변경 로그 탭
// ────────────────────────────────────────────────────────────────────────────
const LOG_COLOR = {
  whiteboard: C.purple,
  file:       C.green,
  revision:   C.blue,
  comment:    C.orange,
  archive:    C.gold,
  optimize:   C.red,
};

function ChangeLogTab({ logs }) {
  const [filter, setFilter] = useState('all');
  const [userFilter, setUserFilter] = useState(null);

  const visible = useMemo(() => {
    let v = logs;
    if (filter !== 'all') v = v.filter(l => l.type === filter);
    if (userFilter) v = v.filter(l => l.actor === userFilter);
    return v;
  }, [logs, filter, userFilter]);

  // 사용자별 통계
  const userStats = useMemo(() => {
    const m = {};
    logs.forEach(l => { m[l.actor] = (m[l.actor] || 0) + 1; });
    return Object.entries(m).sort((a, b) => b[1] - a[1]);
  }, [logs]);

  // 타입별 통계
  const typeStats = useMemo(() => {
    const m = {};
    logs.forEach(l => { m[l.type] = (m[l.type] || 0) + 1; });
    return m;
  }, [logs]);

  const types = [
    ['all', '전체', C.dim],
    ['whiteboard', '화이트보드', C.purple],
    ['file', '파일', C.green],
    ['revision', '수정사항', C.blue],
    ['comment', '댓글', C.orange],
    ['archive', '아카이브', C.gold],
    ['optimize', '최적화', C.red],
  ];

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1200, display: 'grid', gridTemplateColumns: '240px 1fr', gap: 20 }}>
      {/* ── 좌측 사이드바: 사용자/통계 ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ padding: '14px 16px', background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10 }}>
          <div style={{ fontSize: 10, color: C.dim, fontWeight: 800, letterSpacing: 1.2, marginBottom: 10 }}>👤 사용자별 활동</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div onClick={() => setUserFilter(null)}
              style={{ padding: '8px 10px', borderRadius: 6, cursor: 'pointer',
                background: !userFilter ? C.goldBg : 'transparent',
                color: !userFilter ? C.gold : C.text,
                fontSize: 12, fontWeight: 700, display: 'flex', justifyContent: 'space-between' }}>
              <span>모든 사용자</span><span style={{ color: C.dim }}>{logs.length}</span>
            </div>
            {userStats.map(([name, count]) => {
              const col = USERS[name] || C.gold;
              const active = userFilter === name;
              return (
                <div key={name} onClick={() => setUserFilter(active ? null : name)}
                  style={{ padding: '8px 10px', borderRadius: 6, cursor: 'pointer',
                    background: active ? `${col}22` : 'transparent',
                    display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 22, height: 22, borderRadius: '50%', background: `${col}33`, color: col, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, border: `1.5px solid ${col}66` }}>
                    {name[0]}
                  </div>
                  <span style={{ flex: 1, fontSize: 12, color: active ? col : C.text, fontWeight: 700 }}>{name}</span>
                  <span style={{ fontSize: 11, color: C.dim, fontWeight: 700 }}>{count}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div style={{ padding: '14px 16px', background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10 }}>
          <div style={{ fontSize: 10, color: C.dim, fontWeight: 800, letterSpacing: 1.2, marginBottom: 10 }}>📊 액션 분포</div>
          {Object.entries(typeStats).sort((a,b) => b[1] - a[1]).map(([k, v]) => {
            const col = LOG_COLOR[k] || C.dim;
            const max = Math.max(...Object.values(typeStats));
            const pct = (v / max) * 100;
            return (
              <div key={k} style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 3 }}>
                  <span style={{ color: col, fontWeight: 700 }}>{k}</span>
                  <span style={{ color: C.dim, fontWeight: 700 }}>{v}</span>
                </div>
                <div style={{ height: 4, background: C.surface2, borderRadius: 2 }}>
                  <div style={{ width: `${pct}%`, height: '100%', background: col, borderRadius: 2 }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── 우측 로그 리스트 ── */}
      <div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
          {types.map(([k, l, col]) => (
            <button key={k} onClick={() => setFilter(k)}
              style={{ padding: '6px 12px', borderRadius: 14, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                border: `1px solid ${filter === k ? col : C.border}`,
                background: filter === k ? `${col}22` : C.surface,
                color: filter === k ? col : C.dim }}>{l}</button>
          ))}
          {userFilter && (
            <button onClick={() => setUserFilter(null)}
              style={{ padding: '6px 12px', borderRadius: 14, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                border: `1px solid ${C.red}44`, background: C.redBg, color: C.red, marginLeft: 'auto' }}>
              👤 {userFilter} ×
            </button>
          )}
        </div>

        {visible.length === 0 && (
          <div style={{ padding: '40px 0', textAlign: 'center', color: C.dim, fontSize: 13 }}>
            기록된 액션이 없습니다
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {visible.map(log => (
            <div key={log.id} style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
              background: C.surface, borderLeft: `3px solid ${LOG_COLOR[log.type] || C.dim}`,
              borderRadius: '0 6px 6px 0',
            }}>
              <span style={{ fontSize: 10, color: C.dim, fontFamily: 'monospace', minWidth: 64 }}>{log.time}</span>
              <span style={{ fontSize: 11, color: USERS[log.actor] || C.gold, fontWeight: 700, minWidth: 50 }}>{log.actor}</span>
              <span style={{ fontSize: 10, padding: '1px 8px', borderRadius: 4, background: `${LOG_COLOR[log.type] || C.dim}22`, color: LOG_COLOR[log.type] || C.dim, fontWeight: 700, letterSpacing: 0.5, minWidth: 70, textAlign: 'center' }}>
                {log.type}
              </span>
              <span style={{ fontSize: 12, color: C.text, flex: 1 }}>{log.message}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// 메인 앱
// ────────────────────────────────────────────────────────────────────────────
function getInitialTab() {
  if (typeof window === 'undefined') return 'whiteboard';
  const params = new URLSearchParams(window.location.search);
  const t = params.get('tab');
  const valid = ['whiteboard', 'files', 'revisions', 'schedule', 'archive', 'changelog'];
  return valid.includes(t) ? t : 'whiteboard';
}

function PIUMCollabBoard() {
  const proj = useProject();
  const [theme, setTheme] = useState(loadTheme);
  const [chatOpen, setChatOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [helpAuto, setHelpAuto] = useState(false);
  const [snapsOpen, setSnapsOpen] = useState(false);

  // 첫 방문 자동 도움말 (1회만, 닫으면 영구 SEEN)
  useEffect(() => {
    try {
      const seen = localStorage.getItem(HELP_SEEN_KEY);
      if (!seen) {
        // 짧은 지연 후 자동 표시 (다른 컴포넌트 마운트 완료 후)
        const t = setTimeout(() => { setHelpOpen(true); setHelpAuto(true); }, 800);
        return () => clearTimeout(t);
      }
    } catch {}
  }, []);
  const toggleTheme = useCallback(() => {
    setTheme(t => {
      const next = t === 'dark' ? 'light' : 'dark';
      applyTheme(next);
      return next;
    });
  }, []);
  const [tab, setTab] = useState(getInitialTab);
  const [logs, setLogs] = useState([
    { id: 1, time: '09:12', actor: '이팀장', type: 'file',     message: '전북과학교육원_제안서_v3.pptx 업로드' },
    { id: 2, time: '09:25', actor: '박대리', type: 'revision', message: '수정 #2 등록: 예산표 항목 추가' },
    { id: 3, time: '10:01', actor: '김과장', type: 'comment',  message: '레퍼런스 카드에 댓글 작성' },
  ]);

  const addLog = useCallback((actor, type, message) => {
    const now = new Date();
    const time = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    setLogs(prev => [{ id: Date.now(), time, actor, type, message }, ...prev]);
  }, []);

  const TABS = [
    { id: 'whiteboard', label: '🗂 화이트보드' },
    { id: 'files',      label: '📁 파일' },
    { id: 'revisions',  label: '✏️ 수정사항' },
    { id: 'schedule',   label: '📅 일정' },
    { id: 'archive',    label: '📦 아카이브' },
    { id: 'changelog',  label: '📋 변경 로그' },
  ];

  // Teams SDK 초기화 (Teams 컨테이너 안에서만 동작, 일반 브라우저에서는 silent fail)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const teams = await import('@microsoft/teams-js');
        await teams.app.initialize();
        const ctx = await teams.app.getContext();
        if (!cancelled && ctx?.page?.subPageId) {
          const sub = ctx.page.subPageId;
          if (['whiteboard', 'files', 'revisions', 'schedule', 'archive', 'changelog'].includes(sub)) {
            setTab(sub);
          }
        }
      } catch (_) { /* Teams 컨테이너 밖이면 무시 */ }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div style={{ background: C.bg, minHeight: '100vh', color: C.text }}>
      <div style={{ padding: '0 32px', background: C.surface, borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 64, position: 'sticky', top: 0, zIndex: 50 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingRight: 16, borderRight: `1px solid ${C.border}` }}>
            <div style={{ width: 30, height: 30, borderRadius: 8, background: `linear-gradient(135deg, ${C.gold}, ${C.goldHi || C.gold})`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#000', fontWeight: 900, fontSize: 14, letterSpacing: -0.5 }}>P</div>
            <span style={{ fontSize: 15, fontWeight: 900, color: C.gold, letterSpacing: 1.5 }}>PIUM</span>
          </div>
          <ProjectTabs {...proj} />
          <span style={{ fontSize: 10, padding: '3px 10px', borderRadius: 12, background: C.goldBg, color: C.gold, fontWeight: 800, letterSpacing: 0.5 }}>진행중</span>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <GlobalSearch projects={proj.projects} setCurrentId={proj.setCurrentId} setTab={setTab} />
          <button onClick={toggleTheme} title="테마 전환"
            style={{ width: 36, height: 36, borderRadius: 8, border: `1px solid ${C.border}`, background: C.surface2, color: C.text, fontSize: 15, cursor: 'pointer' }}>
            {theme === 'dark' ? '☀' : '🌙'}
          </button>
          <button onClick={() => setSnapsOpen(true)} title="시간별 복원 (자동 백업)"
            style={{ width: 36, height: 36, borderRadius: 8, border: `1px solid ${C.border}`, background: C.surface2, color: C.text, fontSize: 14, cursor: 'pointer' }}>
            🕐
          </button>
          <button onClick={() => { setHelpAuto(false); setHelpOpen(true); }} title="사용방법 안내"
            style={{ width: 36, height: 36, borderRadius: 8, border: `1px solid ${C.border}`, background: C.surface2, color: C.text, fontSize: 15, cursor: 'pointer' }}>
            ❓
          </button>
          <button onClick={() => setChatOpen(o => !o)} title="팀 채팅"
            style={{ width: 36, height: 36, borderRadius: 8, border: `1px solid ${chatOpen ? C.gold : C.border}`, background: chatOpen ? C.goldBg : C.surface2, color: chatOpen ? C.gold : C.text, fontSize: 15, cursor: 'pointer', position: 'relative' }}>
            💬
          </button>
          <div style={{ display: 'flex', gap: 0, alignItems: 'center', paddingLeft: 8, borderLeft: `1px solid ${C.border}` }}>
            {[['이팀장', C.gold], ['박대리', C.blue], ['김과장', C.green]].map(([name, col]) => (
              <div key={name} title={name}
                style={{ width: 28, height: 28, borderRadius: '50%', background: `${col}33`, color: col, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, border: `1.5px solid ${col}66`, marginLeft: -6 }}>
                {name[0]}
              </div>
            ))}
            <div title="+2" style={{ width: 28, height: 28, borderRadius: '50%', background: C.surface2, color: C.dim, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, border: `1.5px solid ${C.border}`, marginLeft: -6 }}>+2</div>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', background: C.surface, borderBottom: `1px solid ${C.border}`, padding: '0 32px', overflowX: 'auto', position: 'sticky', top: 64, zIndex: 49 }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '14px 18px', background: 'transparent', border: 'none',
            borderBottom: tab === t.id ? `2px solid ${C.gold}` : '2px solid transparent',
            color: tab === t.id ? C.gold : C.dim, cursor: 'pointer', fontSize: 13, fontWeight: 700,
            transition: 'all 0.15s', marginBottom: -1, whiteSpace: 'nowrap',
          }}
          onMouseEnter={(e) => { if (tab !== t.id) e.currentTarget.style.color = C.text; }}
          onMouseLeave={(e) => { if (tab !== t.id) e.currentTarget.style.color = C.dim; }}>
            {t.label}
          </button>
        ))}
      </div>

      <div style={{ overflowY: 'auto', marginRight: chatOpen ? 340 : 0, transition: 'margin-right 0.2s' }}>
        {tab === 'whiteboard' && <WhiteboardTab addLog={addLog} data={proj.current?.data || {}} setData={proj.setData} />}
        {tab === 'files'      && <FilesTab addLog={addLog} sendToWhiteboard={(f) => {
          const v = proj.current?.data || {};
          const newCard = {
            id: Date.now(), type: 'file', x: 200, y: 200, w: 220, h: 150,
            title: f.name, content: `${f.size} · ${f.date} · 👤 ${f.uploader}`,
            color: C.green, reactions: {}, comments: [],
            file: { name: f.name, size: f.size, ext: f.type, source: 'tab', tabId: f.id },
            preview: false,
          };
          proj.setData(d => ({ ...d, cards: [...(d.cards || []), newCard] }));
          setTab('whiteboard');
        }} />}
        {tab === 'revisions'  && <RevisionsTab addLog={addLog} />}
        {tab === 'schedule'   && <ScheduleTab projects={proj.projects} currentId={proj.currentId} setCurrentId={proj.setCurrentId} />}
        {tab === 'archive'    && <ArchiveTab addLog={addLog} />}
        {tab === 'changelog'  && <ChangeLogTab logs={logs} />}
      </div>
      <ChatPanel open={chatOpen} onClose={() => setChatOpen(false)} currentProjectName={proj.current?.name} />
      <HelpModal open={helpOpen} onClose={() => { setHelpOpen(false); setHelpAuto(false); }} autoOpened={helpAuto} />
      {snapsOpen && (
        <SnapshotsModal
          snapshots={proj.snapshots}
          takeSnapshot={proj.takeSnapshot}
          restoreSnapshot={proj.restoreSnapshot}
          deleteSnapshot={proj.deleteSnapshot}
          clearAllSnapshots={proj.clearAllSnapshots}
          exportBackup={proj.exportBackup}
          importBackup={proj.importBackup}
          onClose={() => setSnapsOpen(false)} />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 글로벌 검색 - 프로젝트·카드·파일·수정사항·마감일 통합
// ─────────────────────────────────────────────────────────────────────────────
function GlobalSearch({ projects, setCurrentId, setTab }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const results = useMemo(() => {
    if (!q.trim()) return [];
    const lq = q.toLowerCase();
    const out = [];
    projects.forEach(p => {
      if (p.name.toLowerCase().includes(lq)) out.push({ type: '프로젝트', label: p.name, projectId: p.id, tab: 'whiteboard' });
      (p.data?.cards || []).forEach(c => {
        if ((c.title + ' ' + c.content).toLowerCase().includes(lq))
          out.push({ type: '카드', label: c.title, sub: p.name, projectId: p.id, tab: 'whiteboard' });
      });
      (p.data?.dates || []).forEach(d => {
        if (d.label.toLowerCase().includes(lq))
          out.push({ type: '마감일', label: `${d.label} (${d.date})`, sub: p.name, projectId: p.id, tab: 'whiteboard' });
      });
    });
    // 더미 파일/수정사항 (현재 단일 데이터셋)
    if ('전북'.includes(lq) || 'pptx'.includes(lq) || lq.includes('제안')) {
      out.push({ type: '파일', label: '전북과학교육원_제안서_v3.pptx', tab: 'files' });
    }
    return out.slice(0, 12);
  }, [q, projects]);

  return (
    <div style={{ position: 'relative' }}>
      <input value={q} onChange={e => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder="🔍 전체 검색"
        style={{ width: 220, padding: '7px 12px', background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, fontSize: 12, outline: 'none' }} />
      {open && q && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 200 }} />
          <div style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, width: 360, maxHeight: 380, overflowY: 'auto',
            background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, boxShadow: '0 12px 40px rgba(0,0,0,0.6)', zIndex: 201, padding: 6 }}>
            {results.length === 0 ? (
              <div style={{ padding: '20px 12px', color: C.dim, fontSize: 12, textAlign: 'center' }}>검색 결과 없음</div>
            ) : results.map((r, i) => (
              <div key={i} onClick={() => { if (r.projectId) setCurrentId(r.projectId); setTab(r.tab); setOpen(false); setQ(''); }}
                style={{ padding: '8px 12px', borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}
                onMouseEnter={e => e.currentTarget.style.background = C.surface2}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <span style={{ fontSize: 9, padding: '2px 7px', borderRadius: 4, background: C.goldBg, color: C.gold, fontWeight: 800, letterSpacing: 0.5, minWidth: 44, textAlign: 'center' }}>{r.type}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, color: C.text, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.label}</div>
                  {r.sub && <div style={{ fontSize: 10, color: C.dim, marginTop: 1 }}>{r.sub}</div>}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ErrorBoundary - 전체 크래시 시 복구 UI (데이터 초기화 옵션 제공)
// ─────────────────────────────────────────────────────────────────────────────
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) { console.error('App crash:', error, info); }
  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, background: C.bg, color: C.text, fontFamily: 'Pretendard, sans-serif' }}>
        <div style={{ maxWidth: 480, width: '100%', background: C.surface, border: `1px solid ${C.red}66`, borderRadius: 12, padding: 28 }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>⚠️</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: C.text, marginBottom: 8 }}>앱 오류 발생</div>
          <div style={{ fontSize: 13, color: C.dim, marginBottom: 20, lineHeight: 1.6 }}>
            예기치 못한 문제로 화면이 표시되지 않습니다.<br/>
            아래 옵션 중 하나를 선택해 복구하세요.
          </div>
          <div style={{ padding: 12, background: C.surface2, borderRadius: 6, marginBottom: 18, fontSize: 11, color: C.dim, fontFamily: 'monospace', wordBreak: 'break-all' }}>
            {String(this.state.error?.message || this.state.error)}
          </div>
          <div style={{ display: 'flex', gap: 8, flexDirection: 'column' }}>
            <button onClick={() => { this.setState({ error: null }); window.location.reload(); }}
              style={{ padding: '12px 16px', borderRadius: 8, border: `1px solid ${C.gold}`, background: C.goldBg, color: C.gold, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
              🔄 새로고침
            </button>
            <button onClick={() => {
                if (window.confirm('펜 스트로크만 초기화합니다. 카드/영역/마감일은 유지됩니다. 계속할까요?')) {
                  try {
                    const raw = localStorage.getItem('pium-collab:projects');
                    if (raw) {
                      const data = JSON.parse(raw);
                      data.forEach(p => { if (p.data) p.data.paths = []; });
                      localStorage.setItem('pium-collab:projects', JSON.stringify(data));
                    }
                  } catch {}
                  window.location.reload();
                }
              }}
              style={{ padding: '12px 16px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.surface2, color: C.text, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              ✏ 펜 스트로크만 초기화 (안전)
            </button>
            <button onClick={() => {
                if (window.confirm('⚠ 모든 프로젝트 데이터를 삭제하고 처음부터 시작합니다. 계속할까요?')) {
                  try { localStorage.clear(); } catch {}
                  window.location.reload();
                }
              }}
              style={{ padding: '12px 16px', borderRadius: 8, border: `1px solid ${C.red}44`, background: C.redBg, color: C.red, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              🗑 전체 데이터 초기화 (마지막 수단)
            </button>
          </div>
        </div>
      </div>
    );
  }
}

// Wrapper export
function AppWithBoundary() {
  return <ErrorBoundary><PIUMCollabBoard /></ErrorBoundary>;
}

export default AppWithBoundary;
